import { useRef, useEffect } from 'react';
import noop from 'lodash/noop';
import classNames from 'classnames';
import { onEnterOrSpace } from '../../../global/helpers';
import { Tag } from '../../tag/Tag';

import styles from './button.module.scss';
import {
  CSSClassDefinition,
  resolveClassNames,
} from '../../../helpers/classes';

// Do not modify this file for styling purposes.
// Wrap the button.tsx import in a <div> and apply styles
// (margin/padding/flex/flex-basis etc) to that div instead.
//
// Because the button has "width: 100%", to give it its
// natural width simply make the wrapping <div>
// "display:inline-block" or "position: absolute".
// wrapping it in flexbox might also achieve it.
//
// For consistent UI, never give the wrapping <div>
// an exact value for width and height.

type ButtonAriaAttributes = {
  ['aria-controls']?: string;
  ['aria-expanded']?: boolean;
};

export type ButtonThemes =
  | 'contained--on-surface'
  | 'contained--on-surface-delete'
  | 'contained--on-primary'
  | 'contained--on-primary-brand'
  | 'outlined--on-surface'
  | 'outlined--on-surface-delete'
  | 'outlined--on-primary'
  | 'outlined--on-primary-brand'
  | 'text--on-surface'
  | 'text--on-primary'
  | 'text--on-primary-brand'
  | 'toggle-on'
  | 'toggle-off';

export type Props = {
  ariaLabel?: string;
  noAriaLabel?: boolean;
  dataHook?: string; // used for automation testing,
  href?: string;
  target?: '_blank' | '_self';
  isDisabled?: boolean;
  iconLocation?: 'right' | 'left';
  icon?: React.FC<any>;
  isLoading?: boolean;
  isLocalLinkWithOnClick?: boolean;
  onClick?: (
    event: React.SyntheticEvent<HTMLButtonElement>,
  ) => undefined | null | void | Promise<void> | Promise<undefined>;
  onKeyDown?: (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ) => void | null | undefined;
  size?: 'x-large' | 'large' | 'small' | 'x-small';
  text?: string | null;
  theme?: ButtonThemes;
  type?: 'button' | 'submit';
  buttonClassName?: CSSClassDefinition;
  buttonAriaAttributes?: ButtonAriaAttributes;
};

const Button = ({
  ariaLabel,
  noAriaLabel,
  dataHook,
  href,
  target,
  icon: Icon,
  iconLocation = 'right',
  isDisabled,
  isLoading,
  isLocalLinkWithOnClick,
  onClick,
  onKeyDown,
  size = 'large',
  text,
  theme = 'contained--on-surface',
  type,
  buttonClassName,
  buttonAriaAttributes,
}: Props) => {
  const tag = href ? 'a' : isLocalLinkWithOnClick ? 'span' : 'button';

  const buttonRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (buttonRef.current && buttonRef.current.children) {
      Array.from(buttonRef.current.children).forEach(child => {
        if (child.nodeName === 'svg') {
          child.setAttribute('focusable', 'false');
          // for IE11 - https://stackoverflow.com/questions/29393144/how-to-prevent-svg-elements-from-gaining-focus-with-tabs-in-ie11
        }
      });
    }
  });

  return (
    <Tag
      as={tag}
      href={href && !isDisabled && !isLoading ? href : undefined}
      target={href ? target : undefined}
      aria-label={noAriaLabel ? '' : ariaLabel || text || ''}
      role={isLocalLinkWithOnClick ? 'link' : undefined}
      tabIndex={isLocalLinkWithOnClick ? 0 : undefined}
      ref={buttonRef}
      className={classNames(
        styles.button,
        resolveClassNames(buttonClassName, styles),
        Icon && iconLocation === 'left' && styles['button--icon-left'],
        Icon &&
          iconLocation === 'left' &&
          theme.includes('text') &&
          styles[`${theme}--icon-left`],
        (Icon || href || isLocalLinkWithOnClick) &&
          styles[`button--${theme.includes('text') ? 'inline-' : ''}flex`],
        !theme.includes('text') && styles[size],
        styles[theme],
        isLoading && styles.loading,
        !isLoading && styles[`${theme}--loaded`],
        !theme.includes('text') &&
          href &&
          isDisabled &&
          styles[`non-inline-anchor--disabled`],
        theme.includes('text') && isDisabled && styles[`${theme}--disabled`],
      )}
      data-hook={dataHook}
      disabled={isDisabled || isLoading}
      onClick={!isDisabled && !isLoading ? onClick : noop}
      onKeyDown={
        !isDisabled && !isLoading
          ? isLocalLinkWithOnClick
            ? onEnterOrSpace(onClick)
            : onKeyDown
          : noop
      }
      type={type}
      {...buttonAriaAttributes}
    >
      {text}
      {Icon && <Icon />}
      {isLoading && (
        <div
          className={styles['spinner__wrapper']}
          data-testid='button-spinner'
          key='loading'
        >
          <img
            className={styles.spinner}
            src={
              theme === 'contained--on-surface'
                ? require('../../../assets/indeterminate-spinner-light.png')
                : require('../../../assets/indeterminate-spinner.png')
            }
          />
        </div>
      )}
    </Tag>
  );
};

export default Button;
